import {
  type ComponentProps,
  type ElementRef,
  type FC,
  forwardRef,
  type ReactNode,
  useImperativeHandle,
} from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { ChatHeader } from '@/components/ui/chat-header';
import { useAutoScroll } from '@/hooks/use-auto-scroll';
import { ChatProvider, type IChatContext, useChat } from '@/hooks/use-chat';
import { useComposition } from '@/hooks/use-composition';
import { cn } from '@/lib/utils';

interface ChatBodyProps {
  children:
    | ReactNode
    | (({ messages }: { messages: IChatContext['messages'] }) => ReactNode);
}
const ChatBody: FC<ChatBodyProps> = ({ children }) => {
  const { messages } = useChat();
  const scrollRef = useAutoScroll<HTMLDivElement>();

  return (
    <div
      className='mx-1 flex-1 overflow-auto'
      ref={scrollRef}>
      {typeof children === 'function' ? children({ messages }) : children}
    </div>
  );
};
ChatBody.displayName = 'ChatBody';

interface ChatFooterProps {
  children: ReactNode;
  className?: string;
}
const ChatFooter: FC<ChatFooterProps> = ({ children, className }) => (
  <div className={cn(className)}>{children}</div>
);
ChatFooter.displayName = 'ChatFooter';

const chatVariants = cva(
  'bg-chat-background rounded-md h-full w-full flex flex-col gap-1 border'
);
interface ChatProps extends VariantProps<typeof chatVariants> {
  children: ReactNode;
  className?: string;
}
const Chat = forwardRef<IChatContext, ChatProps>(
  ({ children: _children, className, ...props }, ref) => {
    const context = useChat();

    const [_, header, body, footer] = useComposition(
      _children,
      ChatHeader.displayName,
      ChatBody.displayName,
      ChatFooter.displayName
    );

    useImperativeHandle(ref, () => context);

    return (
      <section className={cn(chatVariants(props), className)}>
        <div className='bg-chat-header rounded-t-md border-b'>{header}</div>
        {body}
        {footer}
      </section>
    );
  }
);
Chat.displayName = 'Chat';

const Root = forwardRef<ElementRef<typeof Chat>, ComponentProps<typeof Chat>>(
  (props, ref) => (
    <ChatProvider>
      <Chat
        ref={ref}
        {...props}
      />
    </ChatProvider>
  )
);
Root.displayName = 'Chat';

interface ChatComposition {
  Header: typeof ChatHeader;
  Body: typeof ChatBody;
  Footer: typeof ChatFooter;
}

const RootWithComposition: typeof Root & ChatComposition = Object.assign(Root, {
  Header: ChatHeader,
  Body: ChatBody,
  Footer: ChatFooter,
});

export { RootWithComposition as Chat };
